/* eslint-disable brace-style */
import { ValidationError } from "sequelize"
import {
    OkrObjective,
    OkrObjectiveCreate,
    OkrObjectiveSave,
    OkrObjectiveMove,
    OkrObjectiveClearParents,
    OkrObjectiveOwner,
    SaveOkrObjectiveOwnerType,
    OkrObjectiveTag,
    SaveOkrObjectiveTagType,
} from "server/models"
import { OrderType } from "client/utils"
import {
    GET, POST, DELETE, validate, search, sort,
} from "client/store/utils"
import {
    LangType,
    OkrObjectiveList,
    OkrObjectiveValidation,
    OkrKeyResultList,
    OkrKeyResultIds,
    OkrObjectiveFilters,
} from "client/store"

export const LoadAllOkrObjectives = (sessionId: number) => (
    GET(`/okrObjective/sessionId/${sessionId}`)
    .then((objectives: OkrObjective[]) => {
        const objectiveList: OkrObjectiveList = {}
        objectives.forEach((objective) => {
            objectiveList[objective.id] = objective
        })
        return { objectiveList, objectives }
    })
)

export const LoadOkrObjectiveById = (objectiveId: number, sessionId: number) => (
    GET(`/okrObjective/id/${objectiveId}/sessionId/${sessionId}`)
    .then((objective: OkrObjective) => objective)
)

export const SaveOkrObjective = (
    id: number,
    objective: OkrObjectiveCreate,
    ownerIds: number[],
    tagIds: number[],
    lang: LangType,
) => {
    const data: OkrObjectiveSave = {
        id,
        objective,
    }
    return POST(
        "/okrObjective/save",
        JSON.stringify(data),
    ).then((res) => {
        const newObjective = res as OkrObjective
        const error = res as ValidationError

        const newObjectiveId = newObjective.id || null
        const validation = <OkrObjectiveValidation>validate(error, lang)

        if (validation) {
            return {
                newObjectiveId,
                validation,
                owners: null,
                tags: null,
            }
        }

        const owners: OkrObjectiveOwner[] = []
        ownerIds.forEach((userId) => {
            owners.push({
                objectiveId: newObjectiveId || id,
                userId,
            })
        })

        const tags: OkrObjectiveTag[] = []
        tagIds.forEach((tagId) => {
            tags.push({
                objectiveId: newObjectiveId || id,
                tagId,
            })
        })

        return Promise.all([
            SaveOkrObjectiveOwners(newObjectiveId || id, owners),
            SaveOkrObjectiveTags(newObjectiveId || id, tags),
        ]).then(() => ({
            newObjectiveId,
            validation,
            owners,
            tags,
        }))
    })
}

export const MoveOkrObjective = (data: OkrObjectiveMove) => (
    POST(
        "/okrObjective/move",
        JSON.stringify(data),
    )
)

export const ClearParentsOkrObjective = (data: OkrObjectiveClearParents) => (
    POST(
        "/okrObjective/clearParents",
        JSON.stringify(data),
    )
)

export const DeleteOkrObjective = (objectiveId: number, sessionId: number) => (
    DELETE(`/okrObjective/delete/${objectiveId}/${sessionId}`)
    .then((removedRows: number) => removedRows)
)

export const SortOkrObjectives = (
    objectives: OkrObjective[],
    keyResultList: OkrKeyResultList,
    keyResultIds: OkrKeyResultIds,
    sortColumn: keyof OkrObjective,
    sortOrder: OrderType,
    filters: OkrObjectiveFilters,
): number[] => {
    const objectivesFiltered = objectives.filter((objective) => {
        const krIds = keyResultIds[objective.id] || []
        let showRow = true

        if (filters.title) {
            const showObjective = objective.title.toUpperCase().indexOf(filters.title.toUpperCase()) >= 0
            let showKeyResults = false
            krIds.forEach((id) => {
                const keyResult = keyResultList[id]
                if (keyResult.title.toUpperCase().indexOf(filters.title.toUpperCase()) >= 0) showKeyResults = true
            })
            if (!showObjective && !showKeyResults) showRow = false
        }

        else if (filters.objectiveOwner) {
            let showObjective = false
            objective.owners.forEach((row) => {
                if (row.userId === filters.objectiveOwner) showObjective = true
            })
            showRow = showObjective
        }

        else if (filters.keyResultOwner) {
            let showKeyResults = false
            krIds.forEach((id) => {
                const keyResult = keyResultList[id]
                keyResult.owners.forEach((row) => {
                    if (row.userId === filters.keyResultOwner) showKeyResults = true
                })
            })
            showRow = showKeyResults
        }

        else if (filters.search) {
            const showObjective = search(filters.search, objective)
            let showKeyResults = false
            krIds.forEach((id) => {
                const keyResult = keyResultList[id]
                if (search(filters.search, keyResult)) showKeyResults = true
            })
            if (!showObjective && !showKeyResults) showRow = false
        }
        return showRow
    })

    objectivesFiltered.sort((a, b) => sort(a, b, sortColumn, sortOrder))

    const objectiveIds: number[] = []
    objectivesFiltered.forEach((objective) => objectiveIds.push(objective.id))

    return objectiveIds
}

export const IsOwnerOkrObjective = (objective: OkrObjective, userId: number) => {
    let isOwner = false
    if (objective && objective.owners) {
        objective.owners.forEach((row) => {
            if (row.userId === userId) isOwner = true
        })
    }
    return isOwner
}

export const SaveOkrObjectiveOwners = (
    objectiveId: number,
    owners: OkrObjectiveOwner[],
) => {
    const ownerData: SaveOkrObjectiveOwnerType = {
        objectiveId,
        owners,
    }
    return POST(
        "/okrObjectiveOwner/save",
        JSON.stringify(ownerData),
    )
}

export const SaveOkrObjectiveTags = (
    objectiveId: number,
    tags: OkrObjectiveTag[],
) => {
    const tagData: SaveOkrObjectiveTagType = {
        objectiveId,
        tags,
    }
    return POST(
        "/okrObjectiveTag/save",
        JSON.stringify(tagData),
    )
}
